import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Mail, ShieldCheck, Upload, Trash2, Play, Square, Loader2,
  CheckCircle2, XCircle, AlertTriangle, Info, UserX,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * E-mail em massa — a versão por e-mail da aba Mailing (WhatsApp).
 *
 * Fluxo: verificar remetente (código de 6 dígitos) → importar contatos
 * (CSV/XLSX, parse no navegador) → escrever assunto/corpo com {nome} e
 * {empresa} → ligar o disparo espaçado (2-5 min entre envios, 08h-18h BRT,
 * teto diário com recomendação de aquecimento).
 *
 * Backend: /api/email-massa/* (email_massa.py)
 */

const RE_EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

// FastAPI manda erro de validação (422) como um ARRAY de objetos em
// `detail`, não como string — jogar isso direto num toast.error() quebra a
// renderização (foi o que deixou a tela em branco). Essa função sempre
// devolve uma string segura, seja qual for o formato do erro.
function getErrorMessage(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map(d => d?.msg || d?.type).filter(Boolean);
    if (msgs.length) return msgs.join(" · ");
  }
  return fallback;
}

export default function EmailMassaTab() {
  // ── remetente ──
  const [sender, setSender] = useState({ verified: false, email: "", smtp_ok: true, smtp_dedicado: false });
  const [senderEmail, setSenderEmail] = useState("");
  const [senderCode, setSenderCode] = useState("");
  const [senderStep, setSenderStep] = useState("email"); // email | code
  const [senderBusy, setSenderBusy] = useState(false);

  // ── contatos ──
  const [contacts, setContacts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, sent: 0, failed: 0, optout: 0 });
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [step, setStep] = useState("list"); // list | map
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [colEmail, setColEmail] = useState("");
  const [colName, setColName] = useState("");
  const [colCompany, setColCompany] = useState("");
  const [colCnpj, setColCnpj] = useState("");
  const fileRef = useRef(null);

  // ── disparo ──
  const [schedule, setSchedule] = useState(null);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [dailyLimit, setDailyLimit] = useState(15);
  const [includeMeis, setIncludeMeis] = useState(false);
  const [includeSignature, setIncludeSignature] = useState(false);
  const [signatureHtml, setSignatureHtml] = useState("");

  // ═══ carregamento ═══
  const fetchSender = useCallback(async () => {
    try {
      const { data } = await api.get("/email-massa/sender");
      setSender(data);
    } catch { /* silencioso */ }
  }, []);

  const fetchContacts = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/email-massa/contacts");
      setContacts(data.contacts || []);
      setSummary({ total: data.total, pending: data.pending, sent: data.sent, failed: data.failed, optout: data.optout });
    } catch { if (!silent) toast.error("Erro ao carregar contatos"); }
    finally { if (!silent) setLoading(false); }
  }, []);

  const scheduleLoadedOnceRef = useRef(false);

  const fetchSchedule = useCallback(async () => {
    try {
      const { data } = await api.get("/email-massa/schedule/status");
      setSchedule(data);
      // Só preenche assunto/corpo automaticamente na PRIMEIRA carga da página.
      // Isso roda de novo a cada 20s (polling), e usar closures de "subject"/
      // "body" aqui seria um bug clássico de stale closure — useCallback com
      // deps vazias nunca vê o valor atualizado do state, então a cada poll
      // ele achava que o campo ainda tava vazio e sobrescrevia o que você
      // tinha acabado de digitar com a versão antiga salva no banco.
      if (!scheduleLoadedOnceRef.current) {
        scheduleLoadedOnceRef.current = true;
        if (data.subject_template) setSubject(data.subject_template);
        if (data.body_template) setBody(data.body_template);
        if (typeof data.include_signature === "boolean") setIncludeSignature(data.include_signature);
        if (data.signature_html) setSignatureHtml(data.signature_html);
      }
      if (data.daily_limit) setDailyLimit(data.daily_limit);
      if (typeof data.include_meis === "boolean") setIncludeMeis(data.include_meis);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { fetchSender(); fetchContacts(); fetchSchedule(); }, [fetchSender, fetchContacts, fetchSchedule]);

  useEffect(() => {
    const iv = setInterval(() => { fetchSchedule(); fetchContacts(true); }, 20000);
    return () => clearInterval(iv);
  }, [fetchSchedule, fetchContacts]);

  // ═══ remetente ═══
  const handleRequestCode = async () => {
    if (!RE_EMAIL.test(senderEmail.trim())) { toast.error("Digite um e-mail válido."); return; }
    setSenderBusy(true);
    try {
      await api.post("/email-massa/sender/request-code", { email: senderEmail.trim() });
      setSenderStep("code");
      toast.success("Código enviado! Confira a caixa de entrada (e o spam).");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao enviar o código."));
    } finally { setSenderBusy(false); }
  };

  const handleVerifyCode = async () => {
    setSenderBusy(true);
    try {
      await api.post("/email-massa/sender/verify", { email: senderEmail.trim(), codigo: senderCode.trim() });
      toast.success("Remetente confirmado! ✓");
      setSenderStep("email"); setSenderCode("");
      fetchSender();
    } catch (e) {
      toast.error(getErrorMessage(e, "Código incorreto."));
    } finally { setSenderBusy(false); }
  };

  // ═══ import (mesmo parser robusto da aba Mailing) ═══
  const parseCsvText = (text) => {
    const delimiter = (text.slice(0, 2000).match(/;/g) || []).length >
                       (text.slice(0, 2000).match(/,/g) || []).length ? ";" : ",";
    const rows = [];
    let row = [], field = "", inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else inQuotes = false;
        } else field += c;
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === delimiter) {
        row.push(field); field = "";
      } else if (c === "\n" || c === "\r") {
        if (c === "\r" && text[i + 1] === "\n") i++;
        row.push(field); field = "";
        if (row.length > 1 || row[0] !== "") rows.push(row);
        row = [];
      } else field += c;
    }
    if (field !== "" || row.length) { row.push(field); rows.push(row); }
    return rows;
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const ext = file.name.split(".").pop().toLowerCase();
    let rows = [];
    if (ext === "csv") {
      rows = parseCsvText(await file.text());
    } else if (ext === "xlsx" || ext === "xls") {
      try {
        const XLSX = await import("xlsx");
        const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
        rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: "" });
      } catch {
        toast.error("Erro ao ler planilha. Salve como CSV e tente de novo.");
        return;
      }
    } else {
      toast.error("Formato não suportado. Use CSV, XLS ou XLSX.");
      return;
    }
    if (rows.length < 2) { toast.error("Planilha vazia ou sem dados."); return; }

    const headers = rows[0].map(h => String(h).trim());
    setRawHeaders(headers);
    setRawRows(rows.slice(1));

    // adivinha as colunas: e-mail pelo cabeçalho E pelo conteúdo
    const sample = rows.slice(1, 21);
    const looksLikeEmail = (v) => RE_EMAIL.test(String(v ?? "").trim());
    let gEmail = headers.findIndex(h => /e-?mail|correio/i.test(h));
    if (gEmail < 0) {
      const scored = headers
        .map((_, i) => ({ i, hits: sample.filter(r => looksLikeEmail(r[i])).length }))
        .sort((a, b) => b.hits - a.hits);
      if (scored.length && scored[0].hits > 0) gEmail = scored[0].i;
    }
    const gName = headers.findIndex(h => /nome|name|social|fantasia|contato/i.test(h));
    const gCompany = headers.findIndex(h => /empresa|company|raz[aã]o|fantasia/i.test(h));
    const gCnpj = headers.findIndex(h => /cnpj/i.test(h));

    setColEmail(gEmail >= 0 ? String(gEmail) : "");
    setColName(gName >= 0 ? String(gName) : "");
    setColCompany(gCompany >= 0 && gCompany !== gName ? String(gCompany) : "");
    setColCnpj(gCnpj >= 0 ? String(gCnpj) : "");
    setStep("map");
  };

  const handleConfirmImport = async () => {
    if (colEmail === "") { toast.error("Escolha a coluna de e-mail."); return; }
    setImporting(true);
    try {
      const parsed = rawRows.map(r => ({
        email: String(r[Number(colEmail)] ?? "").trim(),
        name: colName !== "" ? String(r[Number(colName)] ?? "").trim() : "",
        company: colCompany !== "" ? String(r[Number(colCompany)] ?? "").trim() : "",
        cnpj: colCnpj !== "" ? String(r[Number(colCnpj)] ?? "").trim() : "",
      })).filter(c => c.email);
      if (!parsed.length) { toast.error("Nenhum e-mail válido encontrado."); return; }

      const { data } = await api.post("/email-massa/import", { contacts: parsed });
      toast.success(`${data.imported} importados · ${data.skipped} ignorados (inválidos, repetidos ou descadastrados)`);
      setStep("list"); setRawHeaders([]); setRawRows([]);
      fetchContacts();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao importar."));
    } finally { setImporting(false); }
  };

  // ═══ disparo ═══
  const handleStart = async () => {
    if (subject.trim().length < 3) { toast.error("Escreva o assunto do e-mail."); return; }
    if (body.trim().length < 10) { toast.error("Escreva a mensagem do e-mail."); return; }
    if (includeSignature && signatureHtml.length > 300000) {
      toast.error("Assinatura muito grande — reduza o tamanho da imagem (ou da foto embutida) antes de salvar.");
      return;
    }
    setScheduleBusy(true);
    try {
      const { data } = await api.post("/email-massa/schedule/start", {
        subject_template: subject.trim(),
        body_template: body.trim(),
        daily_limit: dailyLimit,
        include_meis: includeMeis,
        include_signature: includeSignature,
        signature_html: signatureHtml,
      });
      toast.success(`Disparo ligado! ${data.pending} pendentes · janela ${data.start_hour}h–${data.end_hour}h`);
      fetchSchedule();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao iniciar o disparo."));
    } finally { setScheduleBusy(false); }
  };

  const handleStop = async () => {
    setScheduleBusy(true);
    try {
      await api.post("/email-massa/schedule/stop");
      toast.success("Disparo pausado.");
      fetchSchedule();
    } catch { toast.error("Erro ao pausar."); }
    finally { setScheduleBusy(false); }
  };

  const handleDeleteContact = async (id) => {
    try {
      await api.delete(`/email-massa/contacts/${id}`);
      fetchContacts(true);
    } catch { toast.error("Erro ao excluir."); }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Excluir TODOS os contatos da lista de e-mail?")) return;
    try {
      const { data } = await api.delete("/email-massa/contacts");
      toast.success(`${data.deleted} contatos removidos.`);
      fetchContacts();
    } catch { toast.error("Erro ao limpar."); }
  };

  const statusBadge = (st) => {
    const map = {
      pending: ["Pendente", "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400"],
      sent:    ["Enviado", "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400"],
      failed:  ["Falhou", "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400"],
      optout:  ["Descadastrado", "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400"],
    };
    const [label, cls] = map[st] || map.pending;
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{label}</span>;
  };

  return (
    <div className="space-y-5">

      {/* ═══ 1. REMETENTE ═══ */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-4 w-4 text-blue-600" />
          <h3 className="font-semibold text-sm">E-mail remetente</h3>
        </div>

        {!sender.smtp_ok && (
          <div className="flex items-start gap-2 rounded-sm bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 px-3 py-2 mb-3 text-xs text-red-700 dark:text-red-300">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Envio de e-mail não configurado no servidor. Fale com o suporte.
          </div>
        )}

        {sender.verified ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="font-medium">{sender.email}</span>
              <span className="text-xs text-zinc-500">— as respostas dos leads caem nessa caixa</span>
            </div>
            <button
              onClick={() => { setSender(s => ({ ...s, verified: false })); setSenderEmail(sender.email); setSenderStep("email"); }}
              className="text-xs text-blue-600 hover:underline"
            >
              Trocar e-mail
            </button>
          </div>
        ) : senderStep === "email" ? (
          <div className="flex gap-2 flex-wrap items-center">
            <input
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              placeholder="seuemail@suaempresa.com.br"
              className="flex-1 min-w-[220px] text-sm px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-sm focus:outline-none focus:border-blue-500"
            />
            <button
              onClick={handleRequestCode}
              disabled={senderBusy}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-sm disabled:opacity-60 flex items-center gap-2"
            >
              {senderBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
              Enviar código
            </button>
            <p className="w-full text-[11px] text-zinc-500 mt-1">
              Enviaremos um código de 6 dígitos pra confirmar que esse e-mail é seu. Ele vira o
              <b> Reply-To</b> das campanhas — quem responder cai direto na sua caixa.
            </p>
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap items-center">
            <input
              value={senderCode}
              onChange={(e) => setSenderCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Código de 6 dígitos"
              inputMode="numeric"
              className="w-44 text-sm px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-sm focus:outline-none focus:border-blue-500 tracking-widest font-mono"
            />
            <button
              onClick={handleVerifyCode}
              disabled={senderBusy || senderCode.length !== 6}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-sm disabled:opacity-60 flex items-center gap-2"
            >
              {senderBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Confirmar
            </button>
            <button onClick={handleRequestCode} disabled={senderBusy} className="text-xs text-blue-600 hover:underline">
              Reenviar código
            </button>
          </div>
        )}
      </div>

      {/* ═══ 2. CONTATOS ═══ */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-sm p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-blue-600" />
            <h3 className="font-semibold text-sm">Lista de contatos</h3>
            <span className="text-xs text-zinc-500">
              {summary.total} no total · {summary.pending} pendentes · {summary.sent} enviados
              {summary.optout > 0 && ` · ${summary.optout} descadastrados`}
            </span>
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx" onChange={handleFile} className="hidden" />
            <button
              onClick={() => fileRef.current?.click()}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-sm flex items-center gap-1.5"
            >
              <Upload className="h-3 w-3" /> Importar CSV/XLSX
            </button>
            {summary.total > 0 && (
              <button
                onClick={handleClearAll}
                className="px-3 py-1.5 text-xs border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded-sm flex items-center gap-1.5 hover:bg-red-50 dark:hover:bg-red-950/40"
              >
                <Trash2 className="h-3 w-3" /> Limpar tudo
              </button>
            )}
          </div>
        </div>

        {/* mapeamento de colunas após escolher o arquivo */}
        {step === "map" && (
          <div className="rounded-sm border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 p-4 mb-4">
            <p className="text-xs mb-3 text-blue-900 dark:text-blue-200">
              Confirme quais colunas usar ({rawRows.length} linhas encontradas):
            </p>
            <div className="grid gap-3 sm:grid-cols-4">
              {[
                ["E-mail *", colEmail, setColEmail],
                ["Nome (opcional)", colName, setColName],
                ["Empresa (opcional)", colCompany, setColCompany],
                ["CNPJ (opcional)", colCnpj, setColCnpj],
              ].map(([label, val, setter]) => (
                <label key={label} className="text-xs">
                  <span className="block mb-1 text-zinc-600 dark:text-zinc-400">{label}</span>
                  <select
                    value={val}
                    onChange={(e) => setter(e.target.value)}
                    className="w-full text-sm px-2 py-1.5 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-sm"
                  >
                    <option value="">— nenhuma —</option>
                    {rawHeaders.map((h, i) => (
                      <option key={i} value={String(i)}>{h || `Coluna ${i + 1}`}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <p className="text-[11px] mt-2 text-blue-800/70 dark:text-blue-300/60">
              Sem coluna de empresa? Se você mapear o CNPJ, o sistema busca automaticamente
              o nome da empresa na Receita Federal antes de cada envio — não precisa preencher os dois.
            </p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleConfirmImport}
                disabled={importing}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-sm disabled:opacity-60 flex items-center gap-2"
              >
                {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                Importar
              </button>
              <button onClick={() => setStep("list")} className="px-4 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-sm">
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* lista */}
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500 py-6 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando...
          </div>
        ) : contacts.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center">
            Nenhum contato ainda. Importe um CSV/XLSX com a coluna de e-mail — o mailing do
            "Comprar Leads" já vem no formato certo.
          </p>
        ) : (
          <div className="max-h-72 overflow-y-auto border border-zinc-100 dark:border-zinc-800 rounded-sm">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-zinc-50 dark:bg-zinc-800 text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">E-mail</th>
                  <th className="text-left px-3 py-2 font-medium">Nome</th>
                  <th className="text-left px-3 py-2 font-medium">Empresa</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {contacts.slice(0, 500).map((c) => (
                  <tr key={c.id} className="border-t border-zinc-100 dark:border-zinc-800">
                    <td className="px-3 py-1.5">{c.email}</td>
                    <td className="px-3 py-1.5 text-zinc-500">{c.name}</td>
                    <td className="px-3 py-1.5 text-zinc-500">{c.company}</td>
                    <td className="px-3 py-1.5">{statusBadge(c.status)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button onClick={() => handleDeleteContact(c.id)} className="text-zinc-400 hover:text-red-500" title="Excluir">
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═══ 3. MENSAGEM + DISPARO ═══ */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4 text-blue-600" />
          <h3 className="font-semibold text-sm">Mensagem e disparo</h3>
          {schedule?.enabled && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400">
              ● Disparando
            </span>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-600 dark:text-zinc-400 block mb-1">Assunto</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              placeholder="Ex: Proposta de internet fibra para {empresa}"
              className="w-full text-sm px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-600 dark:text-zinc-400 block mb-1">Mensagem</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              maxLength={8000}
              placeholder={"Olá {nome}, tudo bem?\n\nSou da ... e queria apresentar ..."}
              className="w-full text-sm px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-sm focus:outline-none focus:border-blue-500 resize-y"
            />
            <p className="text-[11px] text-zinc-500 mt-1">
              Use <code className="px-1 bg-zinc-100 dark:bg-zinc-800 rounded">{"{nome}"}</code> e{" "}
              <code className="px-1 bg-zinc-100 dark:bg-zinc-800 rounded">{"{empresa}"}</code> pra personalizar.
              O rodapé com o link de descadastro é adicionado automaticamente (obrigatório por lei).
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400 cursor-pointer select-none mb-2">
              <input
                type="checkbox"
                checked={includeSignature}
                onChange={(e) => setIncludeSignature(e.target.checked)}
                className="rounded-sm"
              />
              <span>
                Incluir assinatura no e-mail
                <span className="block text-[10.5px] text-zinc-500">
                  Cole o HTML da sua assinatura (a mesma do Zoho, por exemplo). Independente da assinatura
                  configurada no seu webmail — esses e-mails saem por um servidor próprio, então precisa colar aqui também.
                </span>
              </span>
            </label>
            {includeSignature && (
              <>
                <textarea
                  value={signatureHtml}
                  onChange={(e) => setSignatureHtml(e.target.value)}
                  rows={4}
                  placeholder="Cole aqui o código HTML da sua assinatura..."
                  className="w-full text-xs px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-sm focus:outline-none focus:border-blue-500 resize-y font-mono"
                />
                <p className={`text-[10.5px] mt-1 ${signatureHtml.length > 300000 ? "text-red-500" : "text-zinc-500"}`}>
                  {signatureHtml.length.toLocaleString("pt-BR")} caracteres
                  {signatureHtml.length > 300000 && " — muito grande, reduza o tamanho da imagem antes de salvar."}
                </p>
              </>
            )}
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-xs text-zinc-600 dark:text-zinc-400 block mb-1">Envios por dia (máx. 50)</label>
              <input
                type="number" min={1} max={50}
                value={dailyLimit}
                onChange={(e) => setDailyLimit(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                className="w-28 text-sm px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <label className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400 pb-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeMeis}
                onChange={(e) => setIncludeMeis(e.target.checked)}
                className="rounded-sm"
              />
              <span>
                Enviar também para MEIs
                <span className="block text-[10.5px] text-zinc-500">
                  {includeMeis
                    ? "Ativado — envia pra todo mundo, MEI ou não."
                    : "Desativado — pula automaticamente quem for identificado como MEI pelo CNPJ."}
                </span>
              </span>
            </label>

            {schedule?.enabled ? (
              <button
                onClick={handleStop}
                disabled={scheduleBusy}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-sm disabled:opacity-60 flex items-center gap-2"
              >
                {scheduleBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />}
                Pausar disparo
              </button>
            ) : (
              <button
                onClick={handleStart}
                disabled={scheduleBusy || !sender.verified || summary.pending === 0}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-sm disabled:opacity-60 flex items-center gap-2"
              >
                {scheduleBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                Iniciar disparo
              </button>
            )}

            {schedule && (
              <span className="text-xs text-zinc-500 pb-2">
                Hoje: {schedule.sent_today}/{schedule.daily_limit} · janela {schedule.start_hour}h–{schedule.end_hour}h ·
                intervalo de 2–5 min entre envios
              </span>
            )}
          </div>

          {/* aviso de aquecimento */}
          {dailyLimit > 15 && (
            <div className="flex items-start gap-2 rounded-sm bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                <b>Dica de aquecimento:</b> se é sua primeira campanha, comece com 10–15 por dia
                nas primeiras semanas. Volume alto de uma vez em remetente novo aumenta a chance
                de cair no spam — e reputação, uma vez perdida, demora a voltar.
              </span>
            </div>
          )}

          {summary.optout > 0 && (
            <div className="flex items-start gap-2 text-[11px] text-zinc-500">
              <UserX className="h-3 w-3 mt-0.5 shrink-0" />
              {summary.optout} contato(s) se descadastraram e nunca mais receberão seus e-mails, mesmo se reimportados.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
